import { useState } from "react";
import { useParams } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";

import { Button, Group, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconPlus, IconReportMedical } from "@tabler/icons-react";

import { resolveEncounterPlaceOfService } from "@/billing/utils/encounter-place-of-service";
import { getPlaceOfServiceLabel } from "@/billing/utils/place-of-service-label";
import ConfirmationModal from "@/components/ConfirmationModal";
import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";
import { StagedProceduresOperation } from "@/encounters/types/procedures";
import { getEncounterServiceDateKey } from "@/encounters/utils/encounter-timezone";
import PermissionGate from "@/permission/components/PermissionGate";
import { BILLER_MANAGER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";
import { toast } from "@/utils/notifications";

import EncounterBillingHeader from "../EncounterBilling/components/EncounterBillingHeader";
import UpdateLocationDrawer from "../EncounterBilling/components/UpdateLocationDrawer";
import { NoDataContainer } from "../NoDataContainer";
import { ProceduresPicker } from "../ProceduresTab";

import BillerSectionTable from "./components/BillerSectionTable";
import ChargeItemCommentsDrawer from "./components/ChargeItemCommentsDrawer";
import useEncounterChargeItemRows from "./hooks/useEncounterChargeItemRows";
import useStagedBillerSectionProcedures from "./hooks/useStagedBillerSectionProcedures";
import { isStagedBillerSectionRow } from "./utils/staged-biller-section-rows";

/** Named in the picker's toast, so the biller knows which save writes the staged codes. */
const STAGED_SAVE_LABEL = "save the billing info";

/**
 * The confirmation text for removing a row. It says something different for each kind of row.
 *
 * Removing a captured charge voids it when the billing info is saved. The design's own wording is
 * that nothing is deleted: the procedure and diagnosis it was billed under are the encounter's
 * clinical record, and they are left exactly as they are. An entry the biller only staged was never
 * written, so removing it changes nothing on the server.
 */
const buildDeleteDescription = (row: EncounterChargeItemRow): string =>
  isStagedBillerSectionRow(row.id)
    ? `${row.procedureCode} has not been saved yet, so removing it leaves this encounter's billing exactly as it is now.`
    : `Removing ${row.procedureCode} takes it off this encounter's billing when you save the billing info. The procedure and diagnosis it was billed under stay on the encounter's record — nothing is deleted.`;

interface BillerSectionProps {
  encounter: Encounter;
  paymentMethodDisplay: string;
  insuranceDisplay: string | undefined;
  /**
   * Whether staged codes can be written yet. This is true once the encounter is signed and locked,
   * which is when Save Billing Info is offered. Without it, a biller could stage entries that never
   * reach the encounter.
   */
  canAddProcedures: boolean;
}

/** The action a message offers, for the states the biller can do something about. */
type BillerSectionMessageAction = {
  label: string;
  testId: string;
  onClick: () => void;
};

/**
 * Shows a message in place of the table, for the states with no rows: a failed read, or nothing
 * captured yet.
 */
function BillerSectionMessage({
  text,
  testId,
  action,
}: {
  text: string;
  testId: string;
  action?: BillerSectionMessageAction;
}) {
  return (
    <Group
      p="md"
      bg="white"
      data-testid={testId}
      justify="space-between"
      style={{ borderRadius: 8 }}
    >
      <NoDataContainer
        text={text}
        hideAnchor={!action}
        actionTestId={action?.testId}
        linkText={action?.label ?? ""}
        onAnchorClick={action?.onClick ?? (() => {})}
        icon={<IconReportMedical size={18} color="var(--mantine-color-gray-8)" />}
      />
    </Group>
  );
}

/**
 * The Billing tab as the billing pipeline sees it: one row per ChargeItem captured on the
 * encounter. The production section instead reads the CPT-code questionnaire responses.
 *
 * Charges are captured when the encounter is signed and locked, so an encounter still being
 * documented has none yet and shows the empty state. A biller can add more from here: the picker
 * stages what they choose, the table lists it as unsaved, and the tab's Save Billing Info writes it.
 * Every row shows the design's three actions. Delete stages the row's removal for that same save, Edit
 * re-opens the picker on that row's code so the biller can change what it is billed under, and Notes
 * opens a captured row's Practitioner and Biller comments in a read-only drawer.
 */
function BillerSection({
  encounter,
  paymentMethodDisplay,
  insuranceDisplay,
  canAddProcedures,
}: BillerSectionProps) {
  const { clientId, encounterId } = useParams<{ clientId: string; encounterId: string }>();
  const [isPickerOpen, { open: openPicker, close: closePicker }] = useDisclosure(false);
  const [isLocationDrawerOpen, { open: openLocationDrawer, close: closeLocationDrawer }] =
    useDisclosure(false);
  // The row the delete confirmation is open for. The whole row is kept so the confirmation can name
  // its procedure code, and tell a captured charge apart from an entry that was only staged.
  const [deleteTarget, setDeleteTarget] = useState<EncounterChargeItemRow | null>(null);
  // The row the picker is open on, or null when it was opened to add. The whole row is kept because
  // where a finished session goes depends on whether the row is a captured charge or a staged entry.
  const [editRow, setEditRow] = useState<EncounterChargeItemRow | null>(null);
  // The row the Notes drawer is open on, or null when it is closed.
  const [commentsRow, setCommentsRow] = useState<EncounterChargeItemRow | null>(null);
  const { hasBillerManagerPermission } = usePractitionerPermissions();

  const {
    isLoading,
    rows,
    placeOfService: chargesPlaceOfService,
    hasLoadFailed,
    reload,
  } = useEncounterChargeItemRows({
    clientId,
    encounterId,
  });
  // The charges' own Location wins; the encounter's location fills in until a charge carries one.
  // Held back while the charges load, so the header never shows one Location and then another, and
  // after a failed load, when a charge Location may exist that the read could not see.
  const placeOfService =
    isLoading || hasLoadFailed
      ? undefined
      : (chargesPlaceOfService ?? resolveEncounterPlaceOfService(encounter));
  const locationDisplay = getPlaceOfServiceLabel(placeOfService);

  const {
    stagedRows,
    hasStagedChanges,
    stageOperation,
    stagedDeletionIds,
    stageDeletion,
    unstageProcedureCode,
    previewChargeEdits,
    stageChargeEdit,
    restageProcedure,
  } = useStagedBillerSectionProcedures({ clientId, encounterId, reloadRows: reload });

  // Deleting follows Add More: the practitioner has to be allowed to, and the tab has to have a save
  // that can write it. Staging a deletion the tab could never write would leave the biller looking at
  // a row they have removed that keeps coming back.
  const canDeleteRows = canAddProcedures && hasBillerManagerPermission;

  // Editing is gated the same way, and for the same reason: an edit the tab could never write would
  // leave the biller looking at a row that goes back to what it was the moment the page reloads.
  const canEditRows = canDeleteRows;

  const openPickerToAdd = () => {
    setEditRow(null);
    openPicker();
  };

  const openPickerToEdit = (row: EncounterChargeItemRow) => {
    setEditRow(row);
    openPicker();
  };

  const handleClosePicker = () => {
    closePicker();
    setEditRow(null);
  };

  // Where a finished picker session goes. Adding puts a new entry on the section. Editing a captured
  // charge stages the edit against that charge. Editing an entry that is not written yet has no
  // charge to record an edit on, so the session replaces the entry instead.
  const handleStage = (operation: StagedProceduresOperation) => {
    if (!editRow) {
      stageOperation(operation);
      return;
    }

    if (isStagedBillerSectionRow(editRow.id)) restageProcedure(editRow.procedureCode, operation);
    else stageChargeEdit(editRow.id, operation);
  };

  // Staged entries are listed under the charges the encounter has captured, in the order they were
  // added. A charge staged for deletion is already gone from the table, the same way the procedures
  // tab previews a diagnosis the provider has removed. A charge the biller has edited is listed as
  // the edit leaves it, because Save Billing Info is what writes it and the table has to show the
  // biller what they changed in the meantime.
  const displayedRows = [
    ...previewChargeEdits(rows.filter(({ id }) => !stagedDeletionIds.includes(id))),
    ...stagedRows,
  ];
  const hasRows = displayedRows.length > 0;

  // Location lives on the encounter's ChargeItems, so there is nothing to persist an edit onto
  // before the encounter has at least one captured charge. Gated on `rows`, not `displayedRows` —
  // a staged-only entry has no ChargeItem yet, and saving a Location against one would silently
  // write nothing.
  const canEditLocation = canAddProcedures && hasBillerManagerPermission && rows.length > 0;

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    const wasStaged = isStagedBillerSectionRow(deleteTarget.id);

    if (wasStaged) unstageProcedureCode(deleteTarget.procedureCode);
    else stageDeletion(deleteTarget.id);

    setDeleteTarget(null);
    toast({
      title: "Procedure code removed",
      message: wasStaged
        ? "It had not been saved, so nothing was written."
        : "It leaves this encounter's billing when you save the billing info.",
      type: "success",
      autoClose: 3000,
    });
  };

  return (
    <Stack data-testid="biller-section">
      <EncounterBillingHeader
        encounter={encounter}
        locationDisplay={locationDisplay}
        canEditLocation={canEditLocation}
        onEditLocation={openLocationDrawer}
        insuranceDisplay={insuranceDisplay}
        paymentMethodDisplay={paymentMethodDisplay}
      />
      <Stack mt="xl" gap="md">
        <Group align="center" justify="space-between">
          <Group gap="sm" align="center">
            <TextWrapper fw="bold" c="dark.9">
              Procedure Codes
            </TextWrapper>
            {hasStagedChanges && (
              <TextWrapper fz="sm" c="gray.7" data-testid="biller-section-unsaved-changes">
                Unsaved — these are written when you save the billing info
              </TextWrapper>
            )}
          </Group>
          {canAddProcedures && hasRows && (
            <PermissionGate requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}>
              <Button
                size="sm"
                variant="outline"
                color="burgundy.9"
                onClick={openPickerToAdd}
                leftSection={<IconPlus size={16} />}
                data-testid="biller-section-add-procedure-button"
              >
                Add More
              </Button>
            </PermissionGate>
          )}
        </Group>
        {isLoading && <Loading height={120} />}
        {!isLoading && hasLoadFailed && (
          <BillerSectionMessage
            testId="biller-section-load-failed"
            text="The billing codes could not be loaded."
            action={{
              label: "Retry",
              onClick: reload,
              testId: "biller-section-retry-button",
            }}
          />
        )}
        {!isLoading && !hasLoadFailed && !hasRows && (
          <PermissionGate
            requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}
            fallback={
              <BillerSectionMessage
                testId="biller-section-no-data"
                text="No billing codes have been captured for this encounter."
              />
            }
          >
            <BillerSectionMessage
              testId="biller-section-no-data"
              text="No billing codes have been captured for this encounter."
              action={
                canAddProcedures
                  ? {
                      label: "Add Procedure",
                      onClick: openPickerToAdd,
                      testId: "biller-section-add-procedure-link",
                    }
                  : undefined
              }
            />
          </PermissionGate>
        )}
        {!isLoading && !hasLoadFailed && hasRows && (
          <>
            <CptCopyrightNotice size="sm" />
            <BillerSectionTable
              rows={displayedRows}
              onOpenComments={setCommentsRow}
              onEdit={canEditRows ? openPickerToEdit : undefined}
              onDelete={canDeleteRows ? setDeleteTarget : undefined}
            />
          </>
        )}
      </Stack>

      <ProceduresPicker
        requiresProcedure
        opened={isPickerOpen}
        onStage={handleStage}
        onClose={handleClosePicker}
        stagedSaveLabel={STAGED_SAVE_LABEL}
        serviceDate={getEncounterServiceDateKey(encounter)}
        editTarget={editRow && { lockedSide: "procedure", ...editRow.editTarget }}
      />

      <ChargeItemCommentsDrawer
        row={commentsRow}
        opened={commentsRow !== null}
        onClose={() => setCommentsRow(null)}
      />

      <UpdateLocationDrawer
        opened={isLocationDrawerOpen}
        onClose={closeLocationDrawer}
        encounterId={encounterId ?? ""}
        currentPlaceOfService={placeOfService}
        onSaved={() => {
          closeLocationDrawer();
          reload();
        }}
      />

      <ConfirmationModal
        isDeleting={false}
        opened={deleteTarget !== null}
        confirmButtonText="Yes, Remove"
        onConfirm={handleConfirmDelete}
        title="Remove this procedure code?"
        onClose={() => setDeleteTarget(null)}
        modalTestId="biller-section-delete-code-modal"
        cancelButtonTestId="biller-section-delete-code-cancel-button"
        confirmButtonTestId="biller-section-delete-code-confirm-button"
        description={deleteTarget ? buildDeleteDescription(deleteTarget) : ""}
      />
    </Stack>
  );
}

export default BillerSection;
